import { useState, useMemo } from 'react';
import { useRecommendedSources, useRSSSources, useAddRSSSource, useDeleteRSSSource, useManualFetchRSS, useUpdateRSSSource } from '@/hooks/useRSS';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Loader2, 
  Plus, 
  Trash2, 
  RefreshCw, 
  ExternalLink, 
  Rss, 
  Check,
  AlertCircle,
  CheckSquare,
  Square,
  Image,
  ImageOff
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/utils';
import type { RSSSource } from '@/types/rss';

const statusColors: Record<string, string> = {
  active: 'bg-green-500',
  error: 'bg-red-500',
  paused: 'bg-yellow-500',
  disabled: 'bg-gray-500',
};

// Parse batch input for custom sources
interface BatchSource {
  name: string;
  url: string;
  lineNumber: number;
}

const parseBatchInput = (input: string): BatchSource[] => {
  const sources: BatchSource[] = [];
  const lines = input.split('\n').filter(line => line.trim());
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    // Support formats:
    // 1. Name | URL
    // 2. URL (only)
    // 3. Name, URL
    
    let name = '';
    let url = '';
    
    if (trimmed.includes('|')) {
      const parts = trimmed.split('|').map(p => p.trim());
      name = parts[0];
      url = parts[1];
    } else if (trimmed.includes(',')) {
      const parts = trimmed.split(',').map(p => p.trim());
      name = parts[0];
      url = parts[1];
    } else if (trimmed.startsWith('http')) {
      url = trimmed;
      name = new URL(url).hostname.replace(/^www\./, '');
    } else {
      name = trimmed;
    }
    
    if (url) {
      sources.push({ name, url, lineNumber: index + 1 });
    }
  });
  
  return sources;
};

export default function RSSManagerPage() {
  const [activeTab, setActiveTab] = useState('my-sources');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [batchInput, setBatchInput] = useState('');
  const [selectedRecommended, setSelectedRecommended] = useState<Set<string>>(new Set());
  
  const { data: sources, isLoading: sourcesLoading } = useRSSSources();
  const { data: recommended, isLoading: recommendedLoading } = useRecommendedSources();
  const addSource = useAddRSSSource();
  const deleteSource = useDeleteRSSSource();
  const manualFetch = useManualFetchRSS();
  const updateSource = useUpdateRSSSource();
  
  // Flatten all recommended sources without categories
  const allRecommendedSources = useMemo(() => {
    if (!recommended || recommended.length === 0) return [];
    
    const seen = new Set<string>();
    const unique: RSSSource[] = [];
    
    recommended.forEach((category: {category: string, category_name: string, sources: RSSSource[]}) => {
      category.sources.forEach((source: RSSSource) => {
        if (!seen.has(source.url)) {
          seen.add(source.url);
          unique.push({
            ...source,
            category: category.category_name, // Use category name instead of code
          });
        }
      });
    });
    
    return unique;
  }, [recommended]);
  
  // Check if a recommended source is already added
  const isAdded = (url: string) => sources?.some(s => s.url === url);
  
  const handleToggleSelection = (url: string) => {
    const newSet = new Set(selectedRecommended);
    if (newSet.has(url)) {
      newSet.delete(url);
    } else {
      newSet.add(url);
    }
    setSelectedRecommended(newSet);
  };
  
  const handleSelectAll = () => {
    const unadded = allRecommendedSources.filter(s => !isAdded(s.url));
    if (selectedRecommended.size === unadded.length) {
      setSelectedRecommended(new Set());
    } else {
      setSelectedRecommended(new Set(unadded.map(s => s.url)));
    }
  };
  
  const handleAddRecommended = async (source: RSSSource) => {
    await addSource.mutateAsync({
      name: source.name,
      url: source.url,
      description: source.description || '',
      category: 'news',
      language: source.language || 'en',
      tags: source.tags || [],
    });
  };
  
  const handleBatchAddRecommended = async () => {
    const toAdd = allRecommendedSources.filter(s => selectedRecommended.has(s.url));
    
    for (const source of toAdd) {
      await addSource.mutateAsync({
        name: source.name,
        url: source.url,
        description: source.description || '',
        category: 'news',
        language: source.language || 'en',
        tags: source.tags || [],
      });
    }
    
    setSelectedRecommended(new Set());
  };
  
  const handleAddCustom = async () => {
    const parsed = parseBatchInput(batchInput);
    
    for (const source of parsed) {
      if (source.name && source.url) {
        await addSource.mutateAsync({
          name: source.name,
          url: source.url,
          category: 'news',
          language: 'en',
        });
      }
    }
    
    setBatchInput('');
    setShowAddDialog(false);
  };
  
  const batchSources = useMemo(() => parseBatchInput(batchInput), [batchInput]);
  
  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Rss className="w-6 h-6" />
            RSS 订阅管理
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            添加和管理您的 RSS 新闻源
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          添加自定义源
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="my-sources">
            我的订阅
            {sources && sources.length > 0 && (
              <Badge variant="secondary" className="ml-2">{sources.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="recommended">
            推荐源
            {allRecommendedSources.length > 0 && (
              <Badge variant="secondary" className="ml-2">{allRecommendedSources.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="my-sources">
          {sourcesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sources && sources.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sources.map((source: RSSSource) => (
                <Card key={source.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold truncate">{source.name}</h3>
                          <div className={cn('w-2 h-2 rounded-full', statusColors[source.status])} />
                        </div>
                        
                        {source.feed_title && source.feed_title !== source.name && (
                          <p className="text-sm text-muted-foreground truncate">
                            {source.feed_title}
                          </p>
                        )}
                        
                        <p className="text-xs text-muted-foreground truncate mt-1">
                          {source.url}
                        </p>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {source.category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {source.total_articles} 篇文章
                          </span>
                          {source.last_fetch_at && (
                            <span className="text-xs text-muted-foreground">
                              更新于 {formatRelativeTime(source.last_fetch_at)}
                            </span>
                          )}
                        </div>
                        
                        {source.status === 'error' && source.status_message && (
                          <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {source.status_message}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 ml-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => updateSource.mutate({ 
                            id: source.id, 
                            data: { show_images: source.show_images === 1 ? 0 : 1 } 
                          })}
                          disabled={updateSource.isPending}
                          title={source.show_images === 1 ? '隐藏图片' : '显示图片'}
                        >
                          {source.show_images === 1 ? (
                            <Image className="w-4 h-4 text-primary" />
                          ) : (
                            <ImageOff className="w-4 h-4 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => manualFetch.mutate(source.id)}
                          disabled={manualFetch.isPending}
                        >
                          <RefreshCw className={cn('w-4 h-4', manualFetch.isPending && 'animate-spin')} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(source.url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteSource.mutate(source.id)}
                          disabled={deleteSource.isPending}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Rss className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground font-medium">暂无 RSS 订阅</p>
                <p className="text-sm text-muted-foreground mt-2">
                  从推荐源中添加，或添加自定义 RSS 地址
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab('recommended')}
                >
                  浏览推荐源
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="recommended">
          {recommendedLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : allRecommendedSources.length > 0 ? (
            <div className="space-y-4">
              {/* Batch actions bar */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAll}
                    className="gap-2"
                  >
                    {selectedRecommended.size === allRecommendedSources.filter(s => !isAdded(s.url)).length ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    全选
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    已选择 {selectedRecommended.size} 个
                  </span>
                </div>
                {selectedRecommended.size > 0 && (
                  <Button
                    size="sm"
                    onClick={handleBatchAddRecommended}
                    disabled={addSource.isPending}
                  >
                    {addSource.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    批量添加 ({selectedRecommended.size})
                  </Button>
                )}
              </div>
              
              {/* Sources grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {allRecommendedSources.map((source: RSSSource, idx: number) => {
                  const added = isAdded(source.url);
                  const selected = selectedRecommended.has(source.url);
                  
                  return (
                    <Card 
                      key={idx} 
                      className={cn(
                        added && 'border-green-500/50 bg-green-50/50',
                        selected && 'border-primary/50 bg-primary/5'
                      )}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {!added && (
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() => handleToggleSelection(source.url)}
                              className="mt-1"
                            />
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium truncate">{source.name}</h4>
                              {added && (
                                <Badge className="bg-green-500 text-white shrink-0">
                                  <Check className="w-3 h-3 mr-1" />
                                  已添加
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {source.description}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <Badge variant="outline" className="text-xs">
                                {source.category}
                              </Badge>
                              {source.tags?.slice(0, 3).map((tag: string) => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          
                          {!added && !selected && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAddRecommended(source)}
                              disabled={addSource.isPending}
                              className="shrink-0"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              添加
                            </Button>
                          )}
                          
                          {!added && selected && (
                            <Badge variant="outline" className="shrink-0">
                              已选择
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground">暂无推荐源</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Add Custom Source Dialog - Batch Mode */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>批量添加自定义 RSS 源</DialogTitle>
            <DialogDescription>
              每行输入一个源，格式：名称 | URL 或直接输入 URL
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">批量输入</label>
                <span className="text-xs text-muted-foreground">
                  已识别 {batchSources.length} 个源
                </span>
              </div>
              <textarea
                className="w-full h-40 p-3 rounded-md border border-input bg-background text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder={`示例：
BBC News | https://feeds.bbci.co.uk/news/rss.xml
TechCrunch | https://techcrunch.com/feed/
https://example.com/feed.xml
Tech News, https://technews.com/rss`}
                value={batchInput}
                onChange={(e) => setBatchInput(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                支持格式：名称 | URL、名称, URL、或直接输入 URL（自动提取域名作为名称）
              </p>
            </div>
            
            {/* Preview parsed sources */}
            {batchSources.length > 0 && (
              <div className="border rounded-lg p-3 bg-muted/50">
                <h4 className="text-sm font-medium mb-2">识别结果预览</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {batchSources.map((source, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 shrink-0" />
                      <span className="font-medium truncate">{source.name}</span>
                      <span className="text-muted-foreground truncate flex-1">{source.url}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setBatchInput('');
              setShowAddDialog(false);
            }}>
              取消
            </Button>
            <Button 
              onClick={handleAddCustom}
              disabled={batchSources.length === 0 || addSource.isPending}
            >
              {addSource.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  添加中...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  添加 {batchSources.length} 个源
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
